import React, { useEffect, useState } from "react"
import styled from "@emotion/styled"
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
} from "@mui/material"
import { getTierPrograms } from "services/tierProgram"
import { getFullCategories } from "services/category"
import { updateAiBatchItem } from "services/aiPlatform"
import BatchEventEditForm, {
  buildEventFormValues,
  serializeEventFormValues,
} from "./BatchEventEditForm"

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 14px;
    border: 1px solid var(--cms-border);
    background: var(--cms-surface-secondary);
    box-shadow: 0 12px 48px rgba(15, 23, 42, 0.2);
  }

  .MuiDialogTitle-root {
    padding: 20px 24px;
    border-bottom: 1px solid var(--cms-border);
    background: var(--cms-table-head-bg);
    font-weight: 700;
    font-size: 16px;
    color: var(--cms-text-primary);
  }

  .MuiDialogContent-root {
    padding: 24px;
    color: var(--cms-text-primary);

    .batch-info {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 16px;
      margin-bottom: 28px;
      padding: 16px;
      background: linear-gradient(135deg, rgba(132, 70, 173, 0.04) 0%, rgba(168, 85, 247, 0.02) 100%);
      border: 1px solid var(--cms-border);
      border-radius: 10px;

      .info-item {
        display: flex;
        flex-direction: column;
        gap: 4px;

        .label {
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--cms-text-secondary);
        }

        .value {
          font-size: 14px;
          font-weight: 500;
          color: var(--cms-text-primary);
        }

        .badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          width: fit-content;

          &.status-pending {
            background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
            color: white;
          }

          &.status-approved {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            color: white;
          }

          &.status-executed {
            background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
            color: white;
          }

          &.status-rejected {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            color: white;
          }

          &.status-failed {
            background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
            color: white;
          }
        }
      }
    }

    .items-list {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    .item-card {
      border: 1px solid var(--cms-border);
      border-radius: 10px;
      background: var(--cms-surface-secondary);
      overflow: hidden;

      .item-header {
        padding: 14px 16px;
        background: var(--cms-surface-muted);
        border-bottom: 1px solid var(--cms-border);
        font-weight: 600;
        font-size: 14px;
        color: var(--cms-text-primary);
        display: flex;
        gap: 8px;
        align-items: center;

        .badge {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 700;
          background: rgba(132, 70, 173, 0.2);
          color: var(--cms-link-color);
          text-transform: uppercase;

          &.error-badge {
            background: rgba(239, 68, 68, 0.15);
            color: #b91c1c;
            text-transform: none;
          }
        }
      }

      .item-content {
        padding: 16px;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;

        @media (max-width: 768px) {
          grid-template-columns: 1fr;
        }

        &.event-item-content {
          grid-template-columns: 1fr;
        }

        .batch-event-form {
          .MuiFormControl-root,
          .MuiTextField-root {
            margin-bottom: 0;
          }
        }

        .diff-column {
          .column-header {
            font-size: 12px;
            font-weight: 700;
            color: var(--cms-text-secondary);
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 10px;
            padding-bottom: 8px;
            border-bottom: 2px solid var(--cms-border);
          }

          .diff-block {
            margin: 0;
            background: var(--cms-surface-muted);
            border: 1px solid var(--cms-border);
            border-radius: 8px;
            padding: 12px;
            max-height: 300px;
            overflow: auto;
            font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
            font-size: 12px;
            line-height: 1.6;
            color: var(--cms-text-primary);
            white-space: pre-wrap;
            word-break: break-word;

            &::-webkit-scrollbar {
              width: 6px;
            }

            &::-webkit-scrollbar-track {
              background: transparent;
            }

            &::-webkit-scrollbar-thumb {
              background: rgba(132, 70, 173, 0.3);
              border-radius: 3px;

              &:hover {
                background: rgba(132, 70, 173, 0.5);
              }
            }
          }
        }
      }
    }

    .prompt-section {
      margin-top: 20px;
      padding-top: 20px;
      border-top: 1px solid var(--cms-border);

      .section-title {
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: var(--cms-text-secondary);
        margin-bottom: 12px;
      }

      .prompt-text {
        background: var(--cms-surface-muted);
        border: 1px solid var(--cms-border);
        border-radius: 8px;
        padding: 12px;
        font-size: 13px;
        line-height: 1.6;
        color: var(--cms-text-primary);
        white-space: pre-wrap;
        word-break: break-word;
      }
    }
  }

  .MuiDialogActions-root {
    padding: 16px 24px;
    border-top: 1px solid var(--cms-border);
    gap: 10px;
    background: var(--cms-surface-muted);

    button {
      padding: 10px 18px;
      border-radius: 8px;
      font-weight: 600;
      font-size: 14px;
      text-transform: none;
      transition: all 0.2s ease;

      &.close-btn {
        color: var(--cms-text-secondary);
        border: 1px solid var(--cms-border);
        background: var(--cms-surface-secondary);

        &:hover {
          background: linear-gradient(135deg, rgba(132, 70, 173, 0.08) 0%, rgba(168, 85, 247, 0.04) 100%);
          border-color: var(--primary-color-light);
        }
      }

      &.reject-btn {
        color: #ef4444;
        border: 1px solid rgba(239, 68, 68, 0.3);
        background: linear-gradient(135deg, rgba(239, 68, 68, 0.06) 0%, rgba(220, 38, 38, 0.03) 100%);

        &:hover {
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(220, 38, 38, 0.06) 100%);
          border-color: #ef4444;
        }
      }

      &.approve-btn {
        background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        color: white;
        border: none;
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
        }

        &:active {
          transform: translateY(0);
        }
      }
    }
  }

  [data-cms-theme="dark"] & {
    .error-badge {
      color: #fca5a5 !important;
    }
  }
`

const BatchDetailDlg = ({
  open,
  batch,
  onClose,
  canApprove,
  onApprove,
  onReject,
}) => {
  const [tierPrograms, setTierPrograms] = useState([])
  const [categories, setCategories] = useState([])
  const [editedItems, setEditedItems] = useState({})
  const [isSaving, setIsSaving] = useState(false)

  const isPending = batch?.status === "PENDING"
  const canEdit = canApprove && isPending

  useEffect(() => {
    if (!open) {
      return
    }

    Promise.all([getTierPrograms(), getFullCategories()])
      .then(([tierResponse, categoryResponse]) => {
        const programs = tierResponse?.data || tierResponse
        const categoryList =
          (categoryResponse && (categoryResponse.data ?? categoryResponse)) || []

        setTierPrograms(Array.isArray(programs) ? programs : [])
        setCategories(Array.isArray(categoryList) ? categoryList : [])
      })
      .catch((error) => {
        console.error("Failed to load event reference data:", error)
        setTierPrograms([])
        setCategories([])
      })
  }, [open])

  useEffect(() => {
    if (!batch?.items) {
      setEditedItems({})
      return
    }

    const nextEditedItems = {}
    for (const item of batch.items) {
      if (item.entityType === "event") {
        nextEditedItems[item.id] = buildEventFormValues(item.afterJson || {})
      }
    }
    setEditedItems(nextEditedItems)
  }, [batch])

  const persistEditedItems = async () => {
    if (!batch?.items?.length) {
      return
    }

    const updates = batch.items
      .filter((item) => item.entityType === "event" && editedItems[item.id])
      .map((item) =>
        updateAiBatchItem(
          batch.id,
          item.id,
          serializeEventFormValues(editedItems[item.id], item.afterJson || {})
        )
      )

    if (!updates.length) {
      return
    }

    await Promise.all(updates)
  }

  const handleApprove = async () => {
    setIsSaving(true)
    try {
      await persistEditedItems()
      await onApprove(batch.id)
    } finally {
      setIsSaving(false)
    }
  }

  if (!batch) {
    return null
  }

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "PENDING":
        return "status-pending"
      case "APPROVED":
        return "status-approved"
      case "EXECUTED":
        return "status-executed"
      case "REJECTED":
        return "status-rejected"
      case "FAILED":
        return "status-failed"
      default:
        return ""
    }
  }

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Batch #{batch.id} · {batch.changeSummary}
      </DialogTitle>
      <DialogContent dividers>
        <div className="batch-info">
          <div className="info-item">
            <span className="label">Tool</span>
            <span className="value">{batch.toolName}</span>
          </div>
          <div className="info-item">
            <span className="label">Status</span>
            <span className={`badge ${getStatusBadgeClass(batch.status)}`}>
              {batch.status}
            </span>
          </div>
          <div className="info-item">
            <span className="label">Risk Level</span>
            <span className="value">{batch.riskLevel}</span>
          </div>
          <div className="info-item">
            <span className="label">Created</span>
            <span className="value">
              {batch.createdAt
                ? new Date(batch.createdAt).toLocaleString()
                : "—"}
            </span>
          </div>
          {batch.itemCount && (
            <div className="info-item">
              <span className="label">Items</span>
              <span className="value">{batch.itemCount}</span>
            </div>
          )}
        </div>

        <div className="items-list">
          {(batch.items || []).map((item) => {
            const isEventItem = item.entityType === "event"
            const formValues = editedItems[item.id]

            return (
              <div key={item.id} className="item-card">
                <div className="item-header">
                  <span>{item.entityType}</span>
                  <span className="badge">{item.action}</span>
                  {item.entityId && <span>#{item.entityId}</span>}
                  {item.errorMessage && (
                    <span className="badge error-badge">{item.errorMessage}</span>
                  )}
                </div>
                {isEventItem ? (
                  <div className="item-content event-item-content">
                    <div className="diff-column">
                      <div className="column-header">Before</div>
                      <pre className="diff-block">
                        {JSON.stringify(item.beforeJson || {}, null, 2)}
                      </pre>
                    </div>
                    <div className="diff-column">
                      <div className="column-header">
                        {canEdit ? "Editable Proposal" : "After"}
                      </div>
                      {formValues ? (
                        <BatchEventEditForm
                          value={formValues}
                          editable={canEdit}
                          tierPrograms={tierPrograms}
                          categories={categories}
                          onChange={(nextValue) =>
                            setEditedItems((prev) => ({
                              ...prev,
                              [item.id]: nextValue,
                            }))
                          }
                        />
                      ) : (
                        <pre className="diff-block">
                          {JSON.stringify(item.afterJson || {}, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="item-content">
                    <div className="diff-column">
                      <div className="column-header">Before</div>
                      <pre className="diff-block">
                        {JSON.stringify(item.beforeJson || {}, null, 2)}
                      </pre>
                    </div>
                    <div className="diff-column">
                      <div className="column-header">After</div>
                      <pre className="diff-block">
                        {JSON.stringify(item.afterJson || {}, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {batch.prompt && (
          <div className="prompt-section">
            <div className="section-title">Claude Prompt</div>
            <div className="prompt-text">{batch.prompt}</div>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button className="close-btn" onClick={onClose}>
          Close
        </Button>
        {canApprove && batch.status === "PENDING" && (
          <>
            <Button
              className="reject-btn"
              color="error"
              disabled={isSaving}
              onClick={() => onReject(batch.id)}
            >
              Reject Changes
            </Button>
            <Button
              className="approve-btn"
              variant="contained"
              disabled={isSaving}
              onClick={handleApprove}
            >
              ✓ Approve & Execute
            </Button>
          </>
        )}
      </DialogActions>
    </StyledDialog>
  )
}

export default BatchDetailDlg
